import React from "react";
import {
  History, FilePlus2, FileEdit, Trash2, User as UserIcon, Flag, Workflow, TrendingUp,
  MessageSquare, Upload, Download, Users, Calendar, ShieldAlert, Undo2, Archive,
  CheckCircle2, Send, AlertCircle,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";

/** Human-readable label + icon + tone for each action key. */
const ACTION_META = {
  process_created:               { label: "Processo criado",         icon: FilePlus2,    tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  process_updated:               { label: "Processo atualizado",     icon: FileEdit,     tone: "bg-slate-50 text-slate-700 border-slate-200" },
  process_deleted:               { label: "Processo excluído",       icon: Trash2,       tone: "bg-rose-50 text-rose-700 border-rose-200" },
  process_client_updated:        { label: "Dados do cliente atualizados", icon: UserIcon,tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  process_responsible_changed:   { label: "Responsável alterado",    icon: Users,        tone: "bg-sky-50 text-sky-700 border-sky-200" },
  process_priority_changed:      { label: "Prioridade alterada",     icon: Flag,         tone: "bg-amber-50 text-amber-700 border-amber-200" },
  process_status_changed:        { label: "Status alterado",         icon: Workflow,     tone: "bg-violet-50 text-violet-700 border-violet-200" },
  process_workflow_changed:      { label: "Workflow atualizado",     icon: Workflow,     tone: "bg-violet-50 text-violet-700 border-violet-200" },
  process_progress_updated:      { label: "Progresso atualizado",    icon: TrendingUp,   tone: "bg-teal-50 text-teal-700 border-teal-200" },
  process_notes_updated:         { label: "Notas atualizadas",       icon: MessageSquare,tone: "bg-slate-50 text-slate-700 border-slate-200" },
  process_portal_status_changed: { label: "Acesso do portal alterado", icon: ShieldAlert,tone: "bg-orange-50 text-orange-700 border-orange-200" },
  timeline_created:              { label: "Evento de timeline criado", icon: Calendar,   tone: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  timeline_deleted:              { label: "Evento de timeline removido", icon: Trash2,   tone: "bg-rose-50 text-rose-700 border-rose-200" },
  document_uploaded:             { label: "Documento anexado",       icon: Upload,       tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  document_deleted:              { label: "Documento excluído",      icon: Trash2,       tone: "bg-rose-50 text-rose-700 border-rose-200" },
  // AP-014 — WhatsApp notifications
  whatsapp_notification_sent:    { label: "Notificação WhatsApp enviada",   icon: Send,        tone: "bg-lime-50 text-lime-700 border-lime-200" },
  whatsapp_notification_failed:  { label: "Falha ao enviar WhatsApp",       icon: AlertCircle, tone: "bg-rose-50 text-rose-700 border-rose-200" },
  // Semantic aliases derived from status change values (see enrichAction below):
  process_completed:             { label: "Processo concluído",      icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  process_reopened:              { label: "Processo reaberto",       icon: Undo2,        tone: "bg-amber-50 text-amber-700 border-amber-200" },
  process_archived:              { label: "Processo arquivado",      icon: Archive,      tone: "bg-slate-50 text-slate-700 border-slate-200" },
};

const _DONE_KEYS = new Set(["concluido", "concluído", "finalizado", "encerrado", "aprovado"]);
const _ARCHIVED_KEYS = new Set(["arquivado", "archived"]);

/** Derive a friendlier action key when a `process_status_changed` event
 * represents a completion / reopen / archive.  Falls back to the raw action. */
export function enrichAction(entry) {
  if (entry.action === "process_status_changed") {
    const from = (entry.before?.status || "").toString().toLowerCase();
    const to = (entry.after?.status || "").toString().toLowerCase();
    if (_ARCHIVED_KEYS.has(to)) return "process_archived";
    if (_DONE_KEYS.has(to)) return "process_completed";
    if (_DONE_KEYS.has(from) && !_DONE_KEYS.has(to)) return "process_reopened";
  }
  return entry.action;
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

/** Render a compact old-vs-new "chip" pair for value changes. */
function ChangeChips({ before, after }) {
  if (!before && !after) return null;
  const flat = (obj) => {
    if (!obj) return [];
    return Object.entries(obj).map(([k, v]) => [k, v == null ? "—" : (typeof v === "object" ? JSON.stringify(v) : String(v))]);
  };
  const beforeList = flat(before);
  const afterList = flat(after);

  // Try to render key-by-key when both sides have the same keys
  if (beforeList.length && afterList.length && beforeList.length === afterList.length) {
    const beforeMap = Object.fromEntries(beforeList);
    return (
      <div className="mt-2 flex flex-col gap-1 text-[11px]">
        {afterList.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 flex-wrap">
            <span className="text-muted-foreground font-mono">{k}:</span>
            <span className="px-1.5 py-0.5 rounded border border-border bg-rose-50/60 text-rose-800 line-through">
              {beforeMap[k] ?? "—"}
            </span>
            <span className="text-muted-foreground">→</span>
            <span className="px-1.5 py-0.5 rounded border border-border bg-emerald-50/60 text-emerald-800">
              {v}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-1 text-[11px]">
      {beforeList.length > 0 && (
        <div><span className="text-muted-foreground">Antes:</span> <span className="font-mono text-rose-700">{beforeList.map(([k,v]) => `${k}=${v}`).join(", ")}</span></div>
      )}
      {afterList.length > 0 && (
        <div><span className="text-muted-foreground">Depois:</span> <span className="font-mono text-emerald-700">{afterList.map(([k,v]) => `${k}=${v}`).join(", ")}</span></div>
      )}
    </div>
  );
}

/** Single history entry card, timeline-style. */
export function HistoryEntry({ entry, showProcess = false }) {
  const actionKey = enrichAction(entry);
  const meta = ACTION_META[actionKey] || { label: entry.action, icon: History, tone: "bg-slate-50 text-slate-700 border-slate-200" };
  const Icon = meta.icon;
  const extra = entry.extra || {};
  const extraLine =
    entry.action === "document_uploaded" || entry.action === "document_deleted"
      ? (entry.after?.file_name || entry.before?.file_name || extra.document_id || "")
      : entry.action === "timeline_created" || entry.action === "timeline_deleted"
      ? (entry.after?.title || entry.after?.description || entry.before?.title || entry.before?.description || "")
      : "";

  return (
    <li
      data-testid={`history-entry-${entry.id}`}
      className="relative pl-8 pb-4 last:pb-0"
    >
      <span className="absolute left-2.5 top-1 bottom-0 w-px bg-border" aria-hidden="true" />
      <span className={`absolute left-0 top-0 h-6 w-6 rounded-full border ${meta.tone} grid place-items-center`}>
        <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
      </span>
      <div className="rounded-xl border border-border bg-white p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-foreground">{meta.label}</p>
              {showProcess && entry.process_number && (
                <Badge variant="outline" className="text-[10px] font-mono">
                  {entry.process_number}
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              <span data-testid="history-entry-date">{fmtDate(entry.created_at)}</span>
              <span className="mx-1">·</span>
              <span data-testid="history-entry-time">{fmtTime(entry.created_at)}</span>
              <span className="mx-1">·</span>
              por <span className="font-medium text-foreground">{entry.actor_name || entry.actor_email || "—"}</span>
              {entry.actor_role ? <span className="text-muted-foreground/80"> ({entry.actor_role})</span> : null}
            </p>
            {extraLine ? <p className="text-xs text-muted-foreground mt-1 truncate">{extraLine}</p> : null}
            <ChangeChips before={entry.before} after={entry.after} />
          </div>
        </div>
      </div>
    </li>
  );
}

/** Vertical, immutable list of history entries. */
export function HistoryList({ items, showProcess = false, emptyLabel = "Nenhum evento registrado." }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-white px-6 py-8 text-center text-sm text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }
  return (
    <ol className="relative" data-testid="history-list">
      {items.map((e) => <HistoryEntry key={e.id} entry={e} showProcess={showProcess} />)}
    </ol>
  );
}
